import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTaskById } from '@orbit/shared/content-hierarchy';
import { useBusinessIdea } from '../hooks/useBusinessIdea';
import { LoadingPage, TaskComponentFactory, BackToHome } from '../components';

const TaskDetailPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const { businessIdea, loading: businessIdeaLoading } = useBusinessIdea();

  if (!taskId) {
    return (
      <div className="min-h-screen bg-background-dark">
        <BackToHome />
        <main className="p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-6">
              <h1 className="text-2xl font-bold text-white mb-4">No Task Selected</h1>
              <p className="text-text-secondary">No task ID provided.</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const task = getTaskById(taskId);

  if (!task) {
    return (
      <div className="min-h-screen bg-background-dark">
        <BackToHome />
        <main className="p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-6">
              <h1 className="text-2xl font-bold text-white mb-4">Task Not Found</h1>
              <p className="text-text-secondary">
                The task with ID "{taskId}" was not found in the content hierarchy.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (businessIdeaLoading) {
    return (
      <LoadingPage
        title="Loading Task..."
        subtitle="Please wait while we load your task"
        size="medium"
      />
    );
  }

  if (!businessIdea) {
    return (
      <div className="min-h-screen bg-background-dark">
        <BackToHome />
        <main className="p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-background-surface/95 backdrop-blur-sm rounded-lg border border-gray-600 p-6">
              <h1 className="text-2xl font-bold text-white mb-4">Business Idea Not Found</h1>
              <p className="text-text-secondary">
                You need to have a business idea to access tasks. Please create or select a business
                idea first.
              </p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const handleTaskComplete = (completedTaskId: string, data: any) => {
    console.log('Task completed:', completedTaskId, data);
    // TODO: Implement task completion logic
    // This could include:
    // - Updating the business idea with completed task data
    // - Showing a success message
    // - Redirecting to the next task or home
  };

  const handleTaskSave = (savedTaskId: string, data: any) => {
    console.log('Task saved:', savedTaskId, data);
    // TODO: Implement task save logic
    // This could include:
    // - Saving progress to the database
    // - Showing a save confirmation
  };

  return (
    <TaskComponentFactory
      task={task}
      businessIdeaId={businessIdea.id || ''}
      onComplete={handleTaskComplete}
      onSave={handleTaskSave}
    />
  );
};

export default TaskDetailPage;
